import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  XMarkIcon,
  MinusIcon,
  PlusIcon,
  TrashIcon,
  ShoppingBagIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import { useCart } from '../contexts/CartContext';
import './Cart.css';

const Cart: React.FC = () => {
  const { t } = useTranslation();
  const { 
    cartItems, 
    isCartOpen, 
    closeCart, 
    updateQuantity, 
    removeFromCart,
    getItemCount,
    getTotalPrice,
    getTotalSavings
  } = useCart();
  const [isAnimating, setIsAnimating] = useState(false);

  const handleClose = () => {
    setIsAnimating(true);
    setTimeout(() => {
      closeCart();
      setIsAnimating(false);
    }, 300);
  };

  const handleQuantityChange = (productId: string, newQuantity: number) => {
    updateQuantity(productId, newQuantity);
  };

  if (!isCartOpen) return null;

  return (
    <div className={`cart-overlay ${isAnimating ? 'closing' : ''}`} onClick={handleClose}>
      <div 
        className={`cart-panel ${isAnimating ? 'closing' : ''}`} 
        onClick={(e) => e.stopPropagation()}
      >
        {/* Cart Header */}
        <div className="cart-header">
          <div className="cart-title">
            <ShoppingBagIcon className="cart-icon" />
            <h2>{t('cart.shoppingCart')} ({getItemCount()})</h2>
          </div>
          <button className="close-button" onClick={handleClose}>
            <XMarkIcon className="close-icon" />
          </button>
        </div>

        {/* Cart Content */}
        <div className="cart-content">
          {cartItems.length === 0 ? (
            <div className="empty-cart">
              <ShoppingBagIcon className="empty-cart-icon" />
              <h3>{t('cart.yourCartIsEmpty')}</h3>
              <p>{t('cart.addProductsToGetStarted')}</p>
            </div>
          ) : (
            <>
              {/* Cart Items */}
              <div className="cart-items">
                {cartItems.map((item) => (
                  <div key={item.product._id} className="cart-item">
                    <div className="item-image">
                      <img 
                        src={item.product?.images?.[0]} 
                        alt={item.product.productName}
                        className="product-image"
                      />
                    </div>
                    
                    <div className="item-details">
                      <h4 className="item-name">{item.product.productName}</h4>
                      <p className="item-brand">{item.product.brand}</p>
                      <div className="item-price">
                        <span className="current-price">
                        ₪{item.product.salePrice>0? ((typeof item.product.salePrice === 'string' 
                              ? parseFloat(item.product.salePrice) 
                              : item.product.salePrice)).toFixed(2):item.product.price}
                        </span>
                        {item.product.sale && (
                          <span className="original-price">
                    
                                   ₪{typeof item.product.price === 'string' 
                            ? parseFloat(item.product.price).toFixed(2) 
                            : item.product.price.toFixed(2)}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="item-controls">
                      <div className="quantity-controls">
                        <button 
                          className="quantity-button"
                          onClick={() => handleQuantityChange(item.product._id, item.quantity - 1)}
                        >
                          <MinusIcon className="quantity-icon" />
                        </button>
                        <span className="quantity">{item.quantity}</span>
                        <button 
                          className="quantity-button"
                          onClick={() => handleQuantityChange(item.product._id, item.quantity + 1)}
                        >
                          <PlusIcon className="quantity-icon" />
                        </button>
                      </div>
                      
                        <button 
                          className="remove-button"
                          onClick={() => removeFromCart(item.product._id)}
                          title={t('cart.removeItem')}
                        >
                        <TrashIcon className="remove-icon" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Cart Summary */}
              <div className="cart-summary">
                <div className="summary-row">
                  <span>{t('cart.subtotal')}</span>
                  <span>₪{getTotalPrice().toFixed(2)}</span>
                </div>
                
                {getTotalSavings() > 0 && (
                  <div className="summary-row savings">
                    <span>{t('cart.youSave')}</span>
                    <span>-₪{getTotalSavings().toFixed(2)}</span>
                  </div>
                )}
                
                <div className="summary-row total">
                  <span>{t('cart.total')}</span>
                  <span>₪{(getTotalPrice() - getTotalSavings()).toFixed(2)}</span>
                </div>
                
                {/* Checkout Button */}
                <Link 
                  to="/checkout" 
                  className="checkout-button"
                  onClick={handleClose}
                >
                  <span>{t('cart.proceedToCheckout')}</span>
                  <ArrowRightIcon className="checkout-icon" />
                </Link>
              </div>

              {/* Cart Actions */}
              <div className="cart-actions">
                <button className="continue-shopping" onClick={handleClose}>
                  {t('cart.continueShopping')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Cart;
